class CustomProductPage {
  constructor(section) {
    this.section = section;
    this.galleryIndex = 0;
    this.variants = this.readVariants();
    this.bindGallery();
    this.bindGallerySwipe();
    this.bindImageZoom();
    this.bindMobileZoomModal();
    this.bindVariants();
    this.bindQuantity();
    this.bindAccordions();
    this.bindCartForm();
  }

  readVariants() {
    const source = this.section.querySelector('[data-product-variants]');

    if (!source) return [];

    try {
      return JSON.parse(source.textContent);
    } catch (error) {
      return [];
    }
  }

  bindGallery() {
    this.slides = Array.from(this.section.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.from(this.section.querySelectorAll('[data-gallery-thumb]'));
    this.dots = Array.from(this.section.querySelectorAll('[data-gallery-dot]'));

    this.section.querySelector('[data-gallery-prev]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.showGalleryIndex(this.getAdjacentGalleryIndex(-1), -1);
    });

    this.section.querySelector('[data-gallery-next]')?.addEventListener('click', (event) => {
      event.preventDefault();
      this.showGalleryIndex(this.getAdjacentGalleryIndex(1), 1);
    });

    this.thumbs.forEach((thumb) => {
      thumb.addEventListener('click', (event) => {
        event.preventDefault();
        this.showGalleryIndex(Number(thumb.dataset.galleryThumb));
      });
    });

    this.dots.forEach((dot) => {
      dot.addEventListener('click', (event) => {
        event.preventDefault();
        this.showGalleryIndex(Number(dot.dataset.galleryDot));
      });
    });
  }


  bindGallerySwipe() {
    this.mainMedia = this.section.querySelector('.custom-product-page__main-media');

    if (!this.mainMedia || this.slides.length < 2) return;

    const canSwipeGallery = () => window.matchMedia('(max-width: 989px), (pointer: coarse)').matches;

    this.mainMedia.addEventListener('pointerdown', (event) => {
      if (!canSwipeGallery() || event.pointerType === 'mouse') return;

      this.gallerySwipe = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        deltaX: 0,
        deltaY: 0,
        isHorizontal: false,
      };
    });

    this.mainMedia.addEventListener('pointermove', (event) => {
      if (!this.gallerySwipe || this.gallerySwipe.pointerId !== event.pointerId) return;

      this.gallerySwipe.deltaX = event.clientX - this.gallerySwipe.startX;
      this.gallerySwipe.deltaY = event.clientY - this.gallerySwipe.startY;

      const absX = Math.abs(this.gallerySwipe.deltaX);
      const absY = Math.abs(this.gallerySwipe.deltaY);

      if (absX > 12 && absX > absY * 1.25) {
        this.gallerySwipe.isHorizontal = true;
        event.preventDefault();
      }
    });

    const finishSwipe = (event) => {
      if (!this.gallerySwipe || this.gallerySwipe.pointerId !== event.pointerId) return;

      const { deltaX, deltaY, isHorizontal } = this.gallerySwipe;
      const minDistance = Math.min(90, Math.max(40, this.mainMedia.clientWidth * 0.16));

      this.gallerySwipe = null;

      if (!isHorizontal || Math.abs(deltaX) < minDistance || Math.abs(deltaX) < Math.abs(deltaY) * 1.25) return;

      this.showGalleryIndex(this.galleryIndex + (deltaX < 0 ? 1 : -1), deltaX < 0 ? 1 : -1);
    };

    this.mainMedia.addEventListener('pointerup', finishSwipe);
    this.mainMedia.addEventListener('pointercancel', (event) => {
      if (this.gallerySwipe?.pointerId === event.pointerId) {
        this.gallerySwipe = null;
      }
    });
  }

  bindImageZoom() {
    this.mainMedia = this.section.querySelector('.custom-product-page__main-media');

    if (!this.mainMedia) return;

    this.mainMedia.addEventListener('pointerenter', (event) => this.updateImageZoom(event));
    this.mainMedia.addEventListener('pointermove', (event) => this.updateImageZoom(event));
    this.mainMedia.addEventListener('pointerleave', () => this.resetImageZoom());
  }

  updateImageZoom(event) {
    if (!this.mainMedia || event.pointerType === 'touch') return;

    const image = this.mainMedia.querySelector('.custom-product-page__media-slide.is-active img');

    if (!image) return;

    const rect = this.mainMedia.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    image.style.setProperty('--image-zoom-x', `${Math.min(Math.max(x, 0), 100)}%`);
    image.style.setProperty('--image-zoom-y', `${Math.min(Math.max(y, 0), 100)}%`);
    image.classList.add('is-zoomed');
  }

  resetImageZoom() {
    this.section.querySelectorAll('.custom-product-page__media-slide img.is-zoomed').forEach((image) => {
      image.classList.remove('is-zoomed');
      image.style.removeProperty('--image-zoom-x');
      image.style.removeProperty('--image-zoom-y');
    });
  }

  bindMobileZoomModal() {
    this.zoomModal = this.section.querySelector('[data-zoom-modal]');
    if (!this.zoomModal || !this.mainMedia) return;

    this.zoomImage = this.zoomModal.querySelector('[data-zoom-image]');
    this.zoomStage = this.zoomModal.querySelector('[data-zoom-stage]');
    this.zoomContent = this.zoomModal.querySelector('[data-zoom-content]');
    this.zoomLevel = this.zoomModal.querySelector('[data-zoom-level]');
    this.zoomInBtn = this.zoomModal.querySelector('[data-zoom-in]');
    this.zoomOutBtn = this.zoomModal.querySelector('[data-zoom-out]');
    this.zoomResetBtn = this.zoomModal.querySelector('[data-zoom-reset]');
    this.zoomPrevBtn = this.zoomModal.querySelector('[data-zoom-prev]');
    this.zoomNextBtn = this.zoomModal.querySelector('[data-zoom-next]');

    this.zoomScale = 1;
    this.zoomPanX = 0;
    this.zoomPanY = 0;
    this.minZoomScale = 1;
    this.maxZoomScale = 4;
    this.zoomStep = 0.5;

    let tapStart = null;
    this.mainMedia.addEventListener('pointerdown', (event) => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      if (event.target.closest('[data-gallery-prev], [data-gallery-next]')) return;
      tapStart = { x: event.clientX, y: event.clientY, time: Date.now() };
    });

    this.mainMedia.addEventListener('click', (event) => {
      if (!window.matchMedia('(max-width: 749px)').matches) return;
      if (event.target.closest('[data-gallery-prev], [data-gallery-next]')) return;

      if (tapStart) {
        const dx = Math.abs(event.clientX - tapStart.x);
        const dy = Math.abs(event.clientY - tapStart.y);
        const dt = Date.now() - tapStart.time;
        if (dx > 10 || dy > 10 || dt > 600) return;
      }

      this.openMobileZoomModal();
    });

    this.zoomModal.querySelectorAll('[data-zoom-close], [data-zoom-backdrop]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.closeMobileZoomModal();
      });
    });

    this.zoomInBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      this.setZoomScale(this.zoomScale + this.zoomStep);
    });

    this.zoomOutBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      this.setZoomScale(this.zoomScale - this.zoomStep);
    });

    this.zoomResetBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      this.resetModalZoom();
    });

    this.zoomPrevBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      this.showGalleryIndex(this.getAdjacentGalleryIndex(-1), -1);
      this.updateMobileZoomModalImage();
    });

    this.zoomNextBtn?.addEventListener('click', (event) => {
      event.preventDefault();
      this.showGalleryIndex(this.getAdjacentGalleryIndex(1), 1);
      this.updateMobileZoomModalImage();
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isZoomModalOpen()) {
        this.closeMobileZoomModal();
      }
    });

    this.setupModalInteractions();
  }

  isZoomModalOpen() {
    return Boolean(this.zoomModal && !this.zoomModal.hidden && this.zoomModal.classList.contains('is-open'));
  }

  openMobileZoomModal() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    this.updateMobileZoomModalImage();
    this.resetModalZoom();
    this.zoomModal.hidden = false;
    this.zoomModal.classList.add('is-open');
    document.body.style.overflow = 'hidden';
  }

  closeMobileZoomModal() {
    if (!this.zoomModal) return;
    this.zoomModal.classList.remove('is-open');
    this.zoomModal.hidden = true;
    this.resetModalZoom();
    document.body.style.overflow = '';
  }

  updateMobileZoomModalImage() {
    const activeSlide = this.slides[this.galleryIndex];
    if (!activeSlide || !this.zoomImage) return;

    const img = activeSlide.querySelector('img');
    if (!img) return;

    const zoomSrc = activeSlide.dataset.zoomSrc || img.dataset.zoomSrc || img.currentSrc || img.src;
    this.zoomImage.src = zoomSrc;
    this.zoomImage.alt = img.alt || '';
  }

  setZoomScale(newScale, animate = true) {
    this.zoomScale = Math.min(this.maxZoomScale, Math.max(this.minZoomScale, Math.round(newScale * 10) / 10));
    if (this.zoomScale <= 1) {
      this.zoomPanX = 0;
      this.zoomPanY = 0;
    } else {
      this.clampZoomPan();
    }
    this.applyZoomTransform(animate);
  }

  clampZoomPan() {
    if (!this.zoomImage || !this.zoomContent) return;
    const imgWidth = (this.zoomImage.offsetWidth || 300) * this.zoomScale;
    const imgHeight = (this.zoomImage.offsetHeight || 300) * this.zoomScale;
    const containerWidth = this.zoomContent.clientWidth || window.innerWidth;
    const containerHeight = this.zoomContent.clientHeight || (window.innerHeight - 150);

    const maxPanX = Math.max(0, (imgWidth - containerWidth) / 2);
    const maxPanY = Math.max(0, (imgHeight - containerHeight) / 2);

    this.zoomPanX = Math.min(maxPanX, Math.max(-maxPanX, this.zoomPanX));
    this.zoomPanY = Math.min(maxPanY, Math.max(-maxPanY, this.zoomPanY));
  }

  resetModalZoom() {
    this.zoomScale = 1;
    this.zoomPanX = 0;
    this.zoomPanY = 0;
    this.applyZoomTransform(true);
  }

  applyZoomTransform(animate = true) {
    if (!this.zoomImage) return;

    if (!animate) {
      this.zoomStage?.classList.add('is-dragging');
    } else {
      this.zoomStage?.classList.remove('is-dragging');
    }

    this.zoomImage.style.transform = `translate3d(${this.zoomPanX}px, ${this.zoomPanY}px, 0) scale(${this.zoomScale})`;

    if (this.zoomLevel) {
      this.zoomLevel.textContent = `${Math.round(this.zoomScale * 100)}%`;
    }

    if (this.zoomInBtn) {
      this.zoomInBtn.disabled = this.zoomScale >= this.maxZoomScale;
    }

    if (this.zoomOutBtn) {
      this.zoomOutBtn.disabled = this.zoomScale <= this.minZoomScale;
    }
  }

  setupModalInteractions() {
    if (!this.zoomContent) return;

    let isPanning = false;
    let panStartX = 0;
    let panStartY = 0;
    let initialPanX = 0;
    let initialPanY = 0;

    let initialPinchDistance = 0;
    let initialPinchScale = 1;

    let lastTapTime = 0;
    let lastTapX = 0;
    let lastTapY = 0;

    this.zoomContent.addEventListener('touchstart', (event) => {
      if (event.touches.length === 2) {
        isPanning = false;
        initialPinchDistance = Math.hypot(
          event.touches[0].clientX - event.touches[1].clientX,
          event.touches[0].clientY - event.touches[1].clientY
        );
        initialPinchScale = this.zoomScale;
        return;
      }

      if (event.touches.length === 1) {
        const touch = event.touches[0];
        const now = Date.now();
        const distFromLastTap = Math.hypot(touch.clientX - lastTapX, touch.clientY - lastTapY);

        if (now - lastTapTime < 320 && distFromLastTap < 25) {
          event.preventDefault();
          if (this.zoomScale > 1.2) {
            this.resetModalZoom();
          } else {
            this.setZoomScale(2.5, true);
          }
          lastTapTime = 0;
          return;
        }

        lastTapTime = now;
        lastTapX = touch.clientX;
        lastTapY = touch.clientY;

        if (this.zoomScale > 1) {
          isPanning = true;
          panStartX = touch.clientX;
          panStartY = touch.clientY;
          initialPanX = this.zoomPanX;
          initialPanY = this.zoomPanY;
          this.zoomStage?.classList.add('is-dragging');
        }
      }
    }, { passive: false });

    this.zoomContent.addEventListener('touchmove', (event) => {
      if (event.touches.length === 2 && initialPinchDistance > 0) {
        event.preventDefault();
        const currentDistance = Math.hypot(
          event.touches[0].clientX - event.touches[1].clientX,
          event.touches[0].clientY - event.touches[1].clientY
        );
        const newScale = initialPinchScale * (currentDistance / initialPinchDistance);
        this.setZoomScale(newScale, false);
        return;
      }

      if (isPanning && event.touches.length === 1 && this.zoomScale > 1) {
        event.preventDefault();
        const touch = event.touches[0];
        this.zoomPanX = initialPanX + (touch.clientX - panStartX);
        this.zoomPanY = initialPanY + (touch.clientY - panStartY);
        this.clampZoomPan();
        this.applyZoomTransform(false);
      }
    }, { passive: false });

    const endTouch = () => {
      isPanning = false;
      initialPinchDistance = 0;
      this.zoomStage?.classList.remove('is-dragging');
      this.clampZoomPan();
      this.applyZoomTransform(true);
    };

    this.zoomContent.addEventListener('touchend', endTouch);
    this.zoomContent.addEventListener('touchcancel', endTouch);
  }

  getVisibleGalleryIndexes() {
    const indexes = this.slides
      .map((slide, index) => (slide.dataset.variantHidden === 'true' ? null : index))
      .filter((index) => index !== null);

    return indexes.length ? indexes : this.slides.map((_, index) => index);
  }

  getAdjacentGalleryIndex(direction) {
    const visibleIndexes = this.getVisibleGalleryIndexes();

    if (!visibleIndexes.length) return this.galleryIndex;

    const currentVisibleIndex = visibleIndexes.indexOf(this.galleryIndex);
    const safeIndex = currentVisibleIndex >= 0 ? currentVisibleIndex : 0;
    return visibleIndexes[(safeIndex + direction + visibleIndexes.length) % visibleIndexes.length];
  }

  showGalleryIndex(index, direction = null) {
    if (!this.slides.length || this.isGalleryAnimating) return;

    this.resetImageZoom();

    const visibleIndexes = this.getVisibleGalleryIndexes();
    const targetIndex = visibleIndexes.includes(index) ? index : visibleIndexes[0];

    if (targetIndex === undefined) return;

    const currentIndex = this.galleryIndex;
    const nextIndex = targetIndex;

    if (nextIndex === currentIndex) {
      this.updateGalleryControls(nextIndex);
      return;
    }

    const inferredDirection = direction ?? (nextIndex > currentIndex ? 1 : -1);
    const currentSlide = this.slides[currentIndex];
    const nextSlide = this.slides[nextIndex];

    this.galleryIndex = nextIndex;
    this.updateGalleryControls(nextIndex);
    if (this.isZoomModalOpen()) {
      this.updateMobileZoomModalImage();
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!currentSlide || !nextSlide || reduceMotion) {
      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === nextIndex;
        slide.classList.toggle('is-active', isActive);
        slide.hidden = !isActive;
      });
      return;
    }

    this.animateGallerySlides(currentSlide, nextSlide, inferredDirection);
  }

  updateGalleryControls(activeIndex) {
    [...this.thumbs, ...this.dots].forEach((control) => {
      const controlIndex = Number(control.dataset.galleryThumb ?? control.dataset.galleryDot);
      const isActive = controlIndex === activeIndex;
      control.classList.toggle('is-active', isActive);
      control.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  animateGallerySlides(currentSlide, nextSlide, direction) {
    const distance = direction >= 0 ? '100%' : '-100%';
    this.isGalleryAnimating = true;

    currentSlide.hidden = false;
    nextSlide.hidden = false;
    currentSlide.classList.add('is-active', 'is-sliding');
    nextSlide.classList.add('is-active', 'is-sliding');
    nextSlide.style.position = 'absolute';
    nextSlide.style.inset = '0';
    nextSlide.style.display = 'flex';
    nextSlide.style.alignItems = 'center';
    nextSlide.style.justifyContent = 'center';

    const animationOptions = {
      duration: 350,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      fill: 'forwards',
    };

    let animationCleanedUp = false;
    const cleanup = () => {
      if (animationCleanedUp) return;
      animationCleanedUp = true;

      try { outgoing?.cancel(); } catch (e) {}
      try { incoming?.cancel(); } catch (e) {}

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === this.galleryIndex;
        slide.classList.toggle('is-active', isActive);
        slide.classList.remove('is-sliding');
        slide.hidden = !isActive;
        slide.style.transform = '';
        slide.style.opacity = '';
        slide.style.position = '';
        slide.style.inset = '';
        slide.style.display = '';
        slide.style.alignItems = '';
        slide.style.justifyContent = '';
      });

      this.isGalleryAnimating = false;
    };

    let outgoing = null;
    let incoming = null;

    try {
      outgoing = currentSlide.animate(
        [
          { transform: 'translateX(0)', opacity: 1 },
          { transform: `translateX(calc(${distance} * -1))`, opacity: 0.96 },
        ],
        animationOptions
      );

      incoming = nextSlide.animate(
        [
          { transform: `translateX(${distance})`, opacity: 0.96 },
          { transform: 'translateX(0)', opacity: 1 },
        ],
        animationOptions
      );

      incoming.onfinish = cleanup;
      incoming.oncancel = cleanup;

      window.setTimeout(cleanup, 420);
    } catch (e) {
      cleanup();
    }
  }

  normalizeVariantMediaText(value) {
    return (value || '').trim().toLowerCase();
  }

  hasVariantSpecificMedia(variant) {
    if (!variant) return false;
    const variantTitle = this.normalizeVariantMediaText(variant.title);
    const optionValues = (variant.option_values || []).map((val) => this.normalizeVariantMediaText(val));

    return this.slides.some((slide) => {
      if (slide.dataset.sharedMedia === 'true') return false;
      if (slide.dataset.mediaId && variant.featured_media_id && String(slide.dataset.mediaId) === String(variant.featured_media_id)) return true;
      const mediaAlt = this.normalizeVariantMediaText(slide.dataset.variantAlt);
      if (!mediaAlt) return false;
      const productTitle = this.normalizeVariantMediaText(this.section.querySelector('.custom-product-page__title')?.textContent);
      if (mediaAlt === productTitle) return false;
      return mediaAlt === variantTitle || optionValues.includes(mediaAlt);
    });
  }

  mediaBelongsToVariant(element, variant) {
    if (!variant) return true;
    if (element.dataset.sharedMedia === 'true') return true;
    if (element.dataset.mediaId && variant.featured_media_id && String(element.dataset.mediaId) === String(variant.featured_media_id)) return true;

    const mediaAlt = this.normalizeVariantMediaText(element.dataset.variantAlt);
    const productTitle = this.normalizeVariantMediaText(this.section.querySelector('.custom-product-page__title')?.textContent);

    if (!mediaAlt || mediaAlt === productTitle) return true;

    const variantTitle = this.normalizeVariantMediaText(variant.title);
    const optionValues = (variant.option_values || []).map((value) => this.normalizeVariantMediaText(value));

    if (mediaAlt === variantTitle || optionValues.includes(mediaAlt)) return true;

    if (!this.hasVariantSpecificMedia(variant)) return true;

    return false;
  }

  filterGalleryForVariant(variant) {
    const controls = [...this.thumbs, ...this.dots];
    let firstVisibleIndex = null;

    this.slides.forEach((slide, index) => {
      const isVisible = this.mediaBelongsToVariant(slide, variant);
      slide.dataset.variantHidden = isVisible ? 'false' : 'true';

      if (!isVisible) {
        slide.hidden = true;
        slide.classList.remove('is-active', 'is-sliding');
      }

      if (isVisible && firstVisibleIndex === null) {
        firstVisibleIndex = index;
      }
    });

    controls.forEach((control) => {
      const isVisible = this.mediaBelongsToVariant(control, variant);
      control.hidden = !isVisible;
    });

    const visibleCount = this.slides.filter((slide) => slide.dataset.variantHidden !== 'true').length;
    if (visibleCount === 0) {
      this.slides.forEach((slide) => {
        slide.dataset.variantHidden = 'false';
      });
      controls.forEach((control) => {
        control.hidden = false;
      });
      firstVisibleIndex = 0;
    }

    const featuredIndex = variant?.featured_media_id
      ? this.slides.findIndex(
          (slide) => slide.dataset.variantHidden !== 'true' && String(slide.dataset.mediaId) === String(variant.featured_media_id)
        )
      : -1;

    this.showGalleryIndex(featuredIndex >= 0 ? featuredIndex : firstVisibleIndex ?? 0);
  }

  showMedia(mediaId) {
    if (!mediaId) {
      this.showGalleryIndex(this.getVisibleGalleryIndexes()[0] ?? 0);
      return;
    }

    const mediaIndex = this.slides.findIndex(
      (slide) => slide.dataset.variantHidden !== 'true' && slide.dataset.mediaId === String(mediaId)
    );

    if (mediaIndex >= 0) {
      this.showGalleryIndex(mediaIndex);
    } else {
      this.showGalleryIndex(this.getVisibleGalleryIndexes()[0] ?? 0);
    }
  }

  bindAccordions() {
    this.section.querySelectorAll('[data-animated-accordion]').forEach((details) => {
      const summary = details.querySelector('summary');
      const content = details.querySelector('.custom-product-page__accordion-content');

      if (!summary || !content) return;

      this.setExpandableContentState(details, content, details.open);

      summary.addEventListener('click', (event) => {
        event.preventDefault();
        this.toggleExpandableDetails(details, content, !details.open);
      });
    });
  }

  setExpandableContentState(details, content, isOpen) {
    content.style.overflow = 'hidden';
    content.style.height = isOpen ? 'auto' : '0px';
    content.style.opacity = isOpen ? '1' : '0';
    const closedTransform = this.expandableClosedTransform(content);
    content.style.transform = isOpen ? 'translateY(0)' : closedTransform;
    content.style.paddingBlockStart = isOpen ? '' : '0px';
    content.style.paddingBlockEnd = isOpen ? '' : '0px';
    details.classList.toggle('is-open', isOpen);
  }

  toggleExpandableDetails(details, content, shouldOpen) {
    if (details.dataset.animating === 'true' || details.open === shouldOpen) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const closedTransform = this.expandableClosedTransform(content);
    details.dataset.animating = 'true';
    details.classList.add('is-animating');

    if (content.currentSlideAnimation) {
      content.currentSlideAnimation.cancel();
    }

    if (shouldOpen) {
      details.open = true;
      details.classList.add('is-open');
    } else {
      details.classList.remove('is-open');
    }

    if (shouldOpen) {
      content.style.paddingBlockStart = '';
      content.style.paddingBlockEnd = '';
    }

    const computedStyle = window.getComputedStyle(content);
    const openPaddingStart = computedStyle.paddingBlockStart;
    const openPaddingEnd = computedStyle.paddingBlockEnd;
    const startHeight = shouldOpen ? 0 : content.offsetHeight;
    const endHeight = shouldOpen ? content.scrollHeight : 0;
    const startPaddingStart = shouldOpen ? '0px' : openPaddingStart;
    const startPaddingEnd = shouldOpen ? '0px' : openPaddingEnd;
    const endPaddingStart = shouldOpen ? openPaddingStart : '0px';
    const endPaddingEnd = shouldOpen ? openPaddingEnd : '0px';

    if (reduceMotion) {
      this.finishExpandableDetails(details, content, shouldOpen);
      return;
    }

    content.style.overflow = 'hidden';
    content.style.height = `${startHeight}px`;
    content.style.opacity = shouldOpen ? '0' : '1';
    content.style.transform = shouldOpen ? closedTransform : 'translateY(0)';
    content.style.paddingBlockStart = startPaddingStart;
    content.style.paddingBlockEnd = startPaddingEnd;
    content.getBoundingClientRect();

    const animation = content.animate(
      [
        {
          height: `${startHeight}px`,
          opacity: shouldOpen ? 0 : 1,
          transform: shouldOpen ? closedTransform : 'translateY(0)',
          paddingBlockStart: startPaddingStart,
          paddingBlockEnd: startPaddingEnd,
        },
        {
          height: `${endHeight}px`,
          opacity: shouldOpen ? 1 : 0,
          transform: shouldOpen ? 'translateY(0)' : closedTransform,
          paddingBlockStart: endPaddingStart,
          paddingBlockEnd: endPaddingEnd,
        },
      ],
      {
        duration: 420,
        easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
        fill: 'forwards',
      }
    );

    content.currentSlideAnimation = animation;

    animation.onfinish = () => {
      animation.cancel();
      this.finishExpandableDetails(details, content, shouldOpen);
    };

    animation.oncancel = () => {
      delete content.currentSlideAnimation;
    };
  }

  expandableClosedTransform(content) {
    return content.classList.contains('custom-product-page__description-full') ? 'none' : 'translateY(-8px)';
  }

  finishExpandableDetails(details, content, shouldOpen) {
    if (!shouldOpen) {
      details.open = false;
    }

    content.style.height = shouldOpen ? 'auto' : '0px';
    content.style.opacity = shouldOpen ? '1' : '0';
    const closedTransform = this.expandableClosedTransform(content);
    content.style.transform = shouldOpen ? 'translateY(0)' : closedTransform;
    content.style.paddingBlockStart = shouldOpen ? '' : '0px';
    content.style.paddingBlockEnd = shouldOpen ? '' : '0px';
    details.classList.toggle('is-open', shouldOpen);
    details.classList.remove('is-animating');
    delete details.dataset.animating;
    delete content.currentSlideAnimation;
  }

  bindCartForm() {
    this.form = this.section.querySelector('[data-product-form]');

    this.form?.addEventListener('submit', (event) => {
      event.preventDefault();
      this.addToCart();
    });

    this.buyNowButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.buyNow(this.buyNowButton, this.buyNowLabel);
    });

    this.stickyBuyNowButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.buyNow(this.stickyBuyNowButton, this.stickyBuyNowLabel);
    });
  }

  async addToCart() {
    if (!this.form || !this.addButton || this.addButton.disabled) return;

    const previousLabel = this.addLabel?.innerHTML;
    this.addButton.disabled = true;
    if (this.addLabel) this.addLabel.textContent = 'Adding...';

    try {
      const formData = new FormData(this.form);
      const cartItemsComponents = document.querySelectorAll('cart-items-component[data-section-id]');
      const sectionIds = Array.from(cartItemsComponents).map((component) => component.dataset.sectionId).filter(Boolean);

      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
      }

      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Failed to add item');
      }

      document.dispatchEvent(new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          sourceId: this.section.dataset.sectionId,
          data: {
            source: 'custom-product-page',
            itemCount: Number(this.quantityInput?.value) || 1,
            productId: this.section.dataset.productId,
            variantId: this.variantIdInput?.value,
            sections: data.sections,
          },
        },
      }));

      if (this.addLabel) this.addLabel.textContent = 'Added';

      window.setTimeout(() => {
        this.addButton.disabled = false;
        if (this.addLabel && previousLabel) this.addLabel.innerHTML = previousLabel;
      }, 1200);
    } catch (error) {
      if (this.addLabel) this.addLabel.textContent = 'Error adding';

      window.setTimeout(() => {
        this.addButton.disabled = false;
        if (this.addLabel && previousLabel) this.addLabel.innerHTML = previousLabel;
      }, 1800);
    }
  }

  async buyNow(button = this.buyNowButton, label = this.buyNowLabel) {
    const targetButton = button || this.buyNowButton || this.stickyBuyNowButton;
    const targetLabel = label || this.buyNowLabel || this.stickyBuyNowLabel;

    if (!this.form || !targetButton || targetButton.disabled) return;

    const previousLabel = targetLabel?.innerHTML;
    targetButton.disabled = true;
    if (targetLabel) targetLabel.textContent = 'Processing...';

    try {
      const formData = new FormData(this.form);
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Failed to add item');
      }

      window.location.href = (window.Shopify?.routes?.root || '/') + 'checkout';
    } catch (error) {
      if (targetLabel) targetLabel.textContent = 'Error';

      window.setTimeout(() => {
        targetButton.disabled = false;
        if (targetLabel && previousLabel) targetLabel.innerHTML = previousLabel;
      }, 1800);
    }
  }

  bindVariants() {
    this.variantOptions = this.section.querySelector('[data-variant-options]');
    this.variantButtons = Array.from(this.section.querySelectorAll('[data-variant-button]'));
    this.variantIdInput = this.section.querySelector('[data-variant-id-input]');
    this.priceOutput = this.section.querySelector('[data-variant-price]');
    this.addButton = this.section.querySelector('[data-add-to-cart-button]');
    this.addLabel = this.section.querySelector('[data-add-to-cart-label]');
    this.buyNowButton = this.section.querySelector('[data-buy-now-button]');
    this.buyNowLabel = this.section.querySelector('[data-buy-now-label]');
    this.stickyBuyNowButton = this.section.querySelector('[data-sticky-buy-now-button]');
    this.stickyBuyNowLabel = this.section.querySelector('[data-sticky-buy-now-label]');

    this.variantButtons.forEach((button) => {
      button.addEventListener('click', () => {
        const variant = this.variants.find((item) => String(item.id) === button.dataset.variantId);

        if (variant) {
          this.selectVariant(variant);
        }
      });
    });

    this.updateVariantToggle();
    const currentVariant = this.variants.find((item) => String(item.id) === this.variantIdInput?.value) || this.variants[0];
    if (currentVariant) {
      this.updateComboContents(currentVariant);
    }
    window.addEventListener('resize', () => this.updateVariantToggle());
  }

  updateComboContents(variant) {
    if (!variant) return;

    const packs = Array.from(this.section.querySelectorAll('[data-combo-pack]'));
    if (!packs.length) return;

    let hasVisiblePack = false;
    packs.forEach((pack) => {
      const isActive = pack.dataset.comboPack === String(variant.id);
      pack.classList.toggle('is-active', isActive);
      pack.hidden = !isActive;
      if (isActive) hasVisiblePack = true;
    });

    const comboSection = this.section.querySelector('[data-combo-section]');
    if (comboSection) {
      comboSection.hidden = !hasVisiblePack;
    }
  }

  updateVariantToggle() {
    if (!this.variantOptions) return;

    const activeButton = this.variantButtons.find((button) => button.classList.contains('is-active'));

    if (!activeButton) return;

    this.variantOptions.style.setProperty('--selected-variant-left', `${activeButton.offsetLeft}px`);
    this.variantOptions.style.setProperty('--selected-variant-width', `${activeButton.offsetWidth}px`);
  }

  selectVariant(variant) {
    this.variantButtons.forEach((button) => {
      const isActive = button.dataset.variantId === String(variant.id);
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });

    if (this.variantIdInput) {
      this.variantIdInput.value = variant.id;
    }

    const priceHtml = this.formatMoney(variant.price);

    const priceOutputs = this.section.querySelectorAll('[data-variant-price], [data-header-price]');
    priceOutputs.forEach((output) => {
      output.innerHTML = priceHtml;
    });

    const compareOutput = this.section.querySelector('[data-header-compare-price]');
    const discountBadge = this.section.querySelector('[data-header-discount-badge]');
    if (variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price)) {
      if (compareOutput) {
        compareOutput.innerHTML = this.formatMoney(variant.compare_at_price);
        compareOutput.style.display = '';
        compareOutput.hidden = false;
      }
      if (discountBadge) {
        const pct = Math.round(((Number(variant.compare_at_price) - Number(variant.price)) / Number(variant.compare_at_price)) * 100);
        discountBadge.textContent = `Save ${pct}%`;
        discountBadge.style.display = '';
        discountBadge.hidden = false;
      }
    } else {
      if (compareOutput) {
        compareOutput.style.display = 'none';
        compareOutput.hidden = true;
      }
      if (discountBadge) {
        discountBadge.style.display = 'none';
        discountBadge.hidden = true;
      }
    }

    const selectedVariantName = this.section.querySelector('[data-selected-variant-name]');
    if (selectedVariantName) {
      selectedVariantName.textContent = variant.title;
    }

    if (this.addButton) {
      this.addButton.disabled = !variant.available;
    }

    if (this.buyNowButton) {
      this.buyNowButton.disabled = !variant.available;
    }

    if (this.stickyBuyNowButton) {
      this.stickyBuyNowButton.disabled = !variant.available;
    }

    if (this.addLabel && !variant.available) {
      this.addLabel.textContent = window.Shopify?.translation?.soldOut || 'Sold out';
    } else if (this.addLabel) {
      this.addLabel.innerHTML = `${this.addButtonLabel()} | <span data-variant-price>${priceHtml}</span>`;
      this.priceOutput = this.section.querySelector('[data-variant-price]');
    }

    if (this.buyNowLabel && !variant.available) {
      this.buyNowLabel.textContent = window.Shopify?.translation?.soldOut || 'Sold out';
    } else if (this.buyNowLabel) {
      this.buyNowLabel.textContent = 'Buy Now';
    }

    if (this.stickyBuyNowLabel && !variant.available) {
      this.stickyBuyNowLabel.textContent = window.Shopify?.translation?.soldOut || 'Sold out';
    } else if (this.stickyBuyNowLabel) {
      this.stickyBuyNowLabel.textContent = this.stickyBuyNowButton?.dataset.label || 'Buy Now';
    }

    this.updateVariantToggle();
    this.updateQuantityMax(variant);
    this.filterGalleryForVariant(variant);
    this.updateComboContents(variant);
  }

  addButtonLabel() {
    return this.section.querySelector('[data-add-to-cart-button]')?.dataset.label || 'Add to Bag';
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return this.normalizeCurrencyDisplay(window.Shopify.formatMoney(cents, this.section.dataset.moneyFormat));
    }

    return this.normalizeCurrencyDisplay(
      new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: this.section.dataset.currencyCode || window.Shopify?.currency?.active || 'USD',
      }).format(cents / 100)
    );
  }

  normalizeCurrencyDisplay(formattedPrice) {
    if (!formattedPrice) return formattedPrice;

    const currencyCode = this.section.dataset.currencyCode || window.Shopify?.currency?.active || 'INR';
    const currencySymbol = this.section.dataset.currencySymbol || '₹';

    const hasCode = Boolean(currencyCode && formattedPrice.includes(currencyCode));
    const hasSymbol = Boolean(
      (currencySymbol && formattedPrice.includes(currencySymbol)) ||
      formattedPrice.includes('Rs.') ||
      formattedPrice.includes('Rs')
    );

    if (hasCode && hasSymbol) {
      return formattedPrice.replace(new RegExp(currencyCode, 'g'), '').trim().replace(/\s+/g, ' ');
    }

    return formattedPrice;
  }

  bindQuantity() {
    this.quantityInput = this.section.querySelector('[data-quantity-input]');
    this.quantityMinus = this.section.querySelector('[data-quantity-minus]');
    this.quantityPlus = this.section.querySelector('[data-quantity-plus]');

    this.quantityMinus?.addEventListener('click', () => this.setQuantity(Number(this.quantityInput.value) - 1));
    this.quantityPlus?.addEventListener('click', () => this.setQuantity(Number(this.quantityInput.value) + 1));
    this.quantityInput?.addEventListener('input', () => this.setQuantity(Number(this.quantityInput.value)));

    this.updateQuantityButtons();
  }

  setQuantity(value) {
    if (!this.quantityInput) return;

    const min = Number(this.quantityInput.min) || 1;
    const max = Number(this.quantityInput.max) || Infinity;
    const nextValue = Math.min(Math.max(value || min, min), max);

    this.quantityInput.value = nextValue;
    this.updateQuantityButtons();
  }

  updateQuantityMax(variant) {
    if (!this.quantityInput) return;

    if (variant.inventory_management && variant.inventory_policy === 'deny') {
      this.quantityInput.max = Math.max(variant.inventory_quantity, 0);
    } else {
      this.quantityInput.removeAttribute('max');
    }

    this.setQuantity(Number(this.quantityInput.value));
  }

  updateQuantityButtons() {
    if (!this.quantityInput) return;

    const value = Number(this.quantityInput.value) || 1;
    const min = Number(this.quantityInput.min) || 1;
    const max = Number(this.quantityInput.max) || Infinity;

    if (this.quantityMinus) {
      this.quantityMinus.disabled = value <= min;
    }

    if (this.quantityPlus) {
      this.quantityPlus.disabled = value >= max;
    }
  }
}

const initCustomProductPages = () => {
  document.querySelectorAll('[data-custom-product-page]').forEach((section) => {
    if (section.customProductPage) return;

    section.customProductPage = new CustomProductPage(section);
  });
};

initCustomProductPages();
document.addEventListener('shopify:section:load', initCustomProductPages);
